"use client";

import { useSupabaseClient, useUser } from "@/components/SupabaseProvider";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { SupabaseClient } from "@supabase/supabase-js";
import { TextStyle } from "@ziggapp/klap-core/text";
import type { VideoStylePresetModel } from "@ziggapp/klap-core/video";
import { useCallback } from "react";
import { applyReframeFocusVariant } from "../helpers/reframeFocusExperiment";
import {
  DEFAULT_TEXT_STYLE_SHADOW,
  DEFAULT_TEXT_STYLE_STROKE,
  DEFAULT_TEXT_STYLE_THREE_D_EFFECT,
} from "../models/video_model";
import { Database } from "../types/supabase";
import { klap } from "../utils/klap";
import { useReframeFocusVariant } from "./useReframeFocusVariant";


export const VIDEO_STYLE_PRESETS_QUERY_KEY = "video_style_presets";

// Module-level so callers outside the hook (e.g. VideoInput's dialog
// preload) can prefetch the list into the same react-query cache entry.
export const getVideoStylePresetsByAuthorId = async (
  supabaseClient: SupabaseClient<Database>,
  id: string,
) => {
  const { data, error } = await supabaseClient
    .from("video_style_presets")
    .select("*")
    .or(`author_id.eq.${id}, is_klap_preset.eq.true`)
    .order("is_klap_preset", { ascending: true }) // User presets first
    .order("created_at", { ascending: false });

  if (error) throw error;

  return data.map(klap.stylePreset.fromRow);
};

// HOOK
export const useVideoStylePresets = () => {
  const supabaseClient = useSupabaseClient();
  const queryClient = useQueryClient();
  const user = useUser();
  // "reframe-focus-default" experiment: test-arm users see Klap templates
  // with Focus = "Everyone". Applied on read; the cache keeps the stored rows.
  const reframeFocusVariant = useReframeFocusVariant();
  const select = useCallback(
    (presets: VideoStylePresetModel[]) =>
      reframeFocusVariant === "test"
        ? presets.map((preset) =>
            applyReframeFocusVariant(preset, reframeFocusVariant),
          )
        : presets,
    [reframeFocusVariant],
  );

  const insertVideoStylePresetByAuthorId = async (
    update: VideoStylePresetModel,
    id: string,
  ) => {
    const { createdAt, updatedAt, ...rest } = update;
    const { data, error } = await supabaseClient
      .from("video_style_presets")
      .insert(klap.stylePreset.toRow(rest as VideoStylePresetModel))
      .eq("author_id", id)
      .select()
      .single();

    if (error) throw error;

    return klap.stylePreset.fromRow(data);
  };

  const updateVideoStylePresetById = async (
    update: Partial<VideoStylePresetModel>,
    id: string,
  ) => {
    const { data, error } = await supabaseClient
      .from("video_style_presets")
      .update(klap.stylePreset.toRowPatch(update))
      .eq("id", id);

    if (error) throw error;

    return data;
  };

  const deleteVideoStylePresetById = async (id: string) => {
    const { data, error } = await supabaseClient
      .from("video_style_presets")
      .delete()
      .eq("id", id);

    if (error) throw error;

    return data;
  };

  const insertMutation = useMutation({
    mutationKey: [VIDEO_STYLE_PRESETS_QUERY_KEY, user?.id],
    mutationFn: async (update: VideoStylePresetModel) => {
      if (!user) throw new Error("No user");

      return insertVideoStylePresetByAuthorId(update, user.id);
    },
    onError: (error) => {
      console.error(error);
    },
    onSuccess: (created) => {
      if (!user) return;
      // Seed both caches synchronously with the freshly-created preset so any
      // consumer (`useStylePreset(created.id)` for the editor trigger label,
      // `useVideoStylePresets.data` for the panel's userPresets list, the
      // initialData lookup that backs `useStylePreset`) sees it on the very
      // next render. Without this, `editor.updateVideo({ stylePresetId:
      // created.id })` dispatched right after `await insert.mutateAsync(...)`
      // hits an empty per-id cache and a stale list cache (the invalidation
      // refetch hasn't landed yet) — `useStylePreset` falls back to a ~400ms
      // Supabase round-trip during which `appliedTemplate=undefined` flips
      // the trigger label briefly to "Base".
      //
      // Prepending matches the list query's ordering (`is_klap_preset ASC,
      // created_at DESC`): user templates come first, newest at the front.
      queryClient.setQueryData(["style_preset", created.id], created);
      queryClient.setQueryData<VideoStylePresetModel[]>(
        [VIDEO_STYLE_PRESETS_QUERY_KEY, user.id],
        (prev) => (prev ? [created, ...prev] : [created]),
      );
      // Still invalidate so the list eventually reconciles with server data
      // (timestamps, server-side defaults). The seeded entries above carry
      // the UI through the refetch window without flicker.
      queryClient.invalidateQueries({
        queryKey: [VIDEO_STYLE_PRESETS_QUERY_KEY, user.id],
      });
    },
  });

  const updateMutation = useMutation({
    mutationKey: [VIDEO_STYLE_PRESETS_QUERY_KEY, user?.id],
    mutationFn: async ({
      update,
      id,
    }: {
      update: Partial<VideoStylePresetModel>;
      id: string;
    }) => {
      if (!user) throw new Error("No user");

      return updateVideoStylePresetById(update, id);
    },
    onError: (error) => {
      console.error(error);
    },
    onSuccess: (_data, variables) => {
      if (!user) return;
      // Invalidate list
      queryClient.invalidateQueries({
        queryKey: [VIDEO_STYLE_PRESETS_QUERY_KEY, user.id],
      });
      // Invalidate the specific preset query
      if (variables?.id) {
        queryClient.invalidateQueries({
          queryKey: ["style_preset", variables.id],
        });
      }
    },
  });

  const deleteMutation = useMutation({
    mutationKey: [VIDEO_STYLE_PRESETS_QUERY_KEY, user?.id],
    mutationFn: async (id: string) => {
      if (!user) throw new Error("No user");
      return deleteVideoStylePresetById(id);
    },
    onSuccess: (_data, id) => {
      if (!user) return;
      // Synchronously evict the deleted preset from both caches so consumers
      // see the missing-template state on the very next render. Without this,
      // the per-id cache still resolves to the cached preset for ~300-400ms
      // while the invalidation refetch is in flight — long enough for the
      // panel to keep highlighting the deleted card before snapping to
      // "no selection". Symmetric with insertMutation's optimistic seed.
      queryClient.setQueryData(["style_preset", id], null);
      queryClient.setQueryData<VideoStylePresetModel[]>(
        [VIDEO_STYLE_PRESETS_QUERY_KEY, user.id],
        (prev) => (prev ? prev.filter((p) => p.id !== id) : prev),
      );
      // Still invalidate so React Query reconciles with server state in
      // the background. The synchronous eviction above carries the UI.
      queryClient.invalidateQueries({
        queryKey: [VIDEO_STYLE_PRESETS_QUERY_KEY, user.id],
      });
      if (id) {
        queryClient.invalidateQueries({ queryKey: ["style_preset", id] });
      }
    },
  });

  return {
    ...useQuery({
      queryKey: [VIDEO_STYLE_PRESETS_QUERY_KEY, user?.id],
      queryFn: async () => {
        if (!user) throw new Error("No user");
        return await getVideoStylePresetsByAuthorId(supabaseClient, user.id);
      },
      enabled: !!user,
      select,
    }),
    insert: insertMutation,
    update: updateMutation,
    delete: deleteMutation,
  };
};

export const DEFAULT_INTRO_TITLE_TEXT_STYLE: TextStyle = {
  fontFamily: "Open Sans",
  fontSize: 55,
  case: "lowercase",
  fillColor: "#ffffff",
  strokeColor: "#000000",
  fontVariant: "300",
  fontSrc:
    "https://fonts.gstatic.com/s/opensans/v40/memSYaGs126MiZpBA-UvWbX2vVnXBbObj2OVZyOOSr4dVJWUgshZ1y4nY1M2xLER.ttf",
  shadow: DEFAULT_TEXT_STYLE_SHADOW,
  stroke: { ...DEFAULT_TEXT_STYLE_STROKE("#000000"), width: 0.14 },
  threeDEffect: {
    ...DEFAULT_TEXT_STYLE_THREE_D_EFFECT("#000000"),
    disabled: true,
  },
};
